import React, { useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Home, ShieldCheck, Clock, FileText } from 'lucide-react';

const brandRed  = "#b22335";
const brandNavy = "#3D3B6D";

const faqStyles = {
  section: {
    backgroundColor: 'white',
    padding: '30px 0',
  },
  subtitleWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    color: '#2b4d8d',
    fontFamily: "'hauora-medium', sans-serif",
    fontSize: '15px',
    padding: '8px 25px',
    borderRadius: '50px',
    letterSpacing: '0.3px',
    boxShadow: '0 0 20px rgba(178, 35, 53, 0.25)',
    border: '1px solid rgba(178, 35, 53, 0.1)',
  },
  dot: {
    height: '8px',
    width: '8px',
    backgroundColor: brandRed,
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: '12px',
    flexShrink: 0,
  },
  heading: {
    fontFamily: "'hauora-medium', sans-serif",
    color: brandNavy,
  },
  headingRed: {
    color: brandRed,
  },
  questionText: {
    fontFamily: "'hauora-medium', sans-serif",
    fontSize: 'clamp(1rem, calc(1rem + 0.55vw), 1rem)',
    color: brandNavy,
  },
  answerText: {
    fontFamily: "'hauora-medium', sans-serif",
    fontSize: '15px',
    color: '#4d5663',
    padding: '0 24px 24px 64px',
  },
  btnBase: {
    width: '100%',
    padding: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'none',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
  },
};

/* Responsive heading via CSS injection - avoids a separate file */
const ResponsiveStyle = () => (
  <style>{`
    
    .faq-section-wrap {
      background-color: white;
      padding: 30px 10px;
    }
    @media (max-width: 991px) {
      .faq-section-wrap { padding: 20px 10px; }
    }
    @media (max-width: 576px) {
      .faq-section-wrap { padding: 20px 10px; }
      .faq-answer-text  { padding: 0 16px 20px 16px !important; font-size: 15px !important; }
      .faq-question-btn { padding: 16px !important; }
      .faq-subtitle     { font-size: 13px !important; padding: 7px 18px !important; }
    }
  `}</style>
);

const Faq = () => {
  const [activeId, setActiveId] = useState(null);

  const faqs = [
    {
      id: 1,
      icon: <Home size={20} />,
      question: "Do you provide free roof inspections?",
      answer: "Yes. We offer free roof inspections for homeowners and business owners. Our inspections include a full assessment of shingles, flashing, ventilation, gutters, and structural components. We look for leaks, storm damage, aging materials, and hidden issues that may cause future problems. After the inspection, we provide a clear explanation of our findings along with honest recommendations—whether that’s repair, maintenance, or replacement. There’s no obligation, and our goal is to help you make informed decisions about your roof’s condition and longevity."
    },
    {
      id: 2,
      icon: <ShieldCheck size={20} />,
      question: "How do I find the best roofing contractor near me in Hilliard, Ohio?",
      answer: "Start by checking experience, certifications, customer reviews, and local presence. A reputable contractor should be licensed, insured, and willing to provide references. Look for transparent estimates, written warranties, and clear communication. Avoid contractors who pressure you into quick decisions or request full payment upfront. Local expertise matters—contractors familiar with Ohio weather understand common roofing issues and proper material selection. Choosing a trusted local company ensures accountability, faster service, and long-term support."
    },
    {
      id: 3,
      icon: <Clock size={20} />,
      question: "What roofing materials are best for Ohio weather?",
      answer: "Ohio roofs need materials that withstand wind, hail, temperature changes, and moisture. Architectural asphalt shingles are popular for affordability and durability. Metal roofing offers excellent longevity and storm resistance. For commercial properties, TPO and EPDM provide flexibility and weather protection. The best material depends on budget, building type, and long-term goals."
    },
    {
      id: 4,
      icon: <FileText size={20} />,
      question: "How do I know if I need a roof repair or replacement?",
      answer: "Minor leaks, missing shingles, or localized damage often require repairs. Widespread damage, frequent leaks, sagging areas, or roofs over 20 years old may need replacement. A professional inspection is the best way to determine the most cost-effective solution."
    },
    {
      id: 5,
      icon: <Home size={20} />,
      question: "How long does a roof installation take?",
      answer: "Most residential roof installations take one to three days, depending on size, materials, and weather. Commercial projects vary based on complexity. We provide clear timelines before starting any work."
    },
    {
      id: 6,
      icon: <ShieldCheck size={20} />,
      question: "How much does roof replacement cost in Hilliard, Ohio?",
      answer: "Costs depend on roof size, material type, labor, and complexity. Asphalt shingles are typically more affordable, while metal or specialty systems cost more. We provide detailed, upfront estimates after inspection."
    },
    {
      id: 7,
      icon: <Clock size={20} />,
      question: "Do you offer emergency roof repair near me?",
      answer: "Yes. We provide 24/7 emergency roof repair for storm damage, leaks, and urgent roofing issues. Our rapid response helps prevent further damage and protects your property."
    }
  ];

  return (
    <>
      <ResponsiveStyle />

      <div className="faq-section-wrap cs-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9}>
              {/* Section Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center"
              >
                {/* Subtitle pill */}
                <div className="cs-badge">
                  <span className="faq-subtitle" style={faqStyles.subtitleWrap}>
                    <span style={faqStyles.dot}></span>
                    Hilliard, Ohio Roofing Contractors FAQ
                  </span>
                </div>

                {/* Main heading */}
                <h2 className="cs-title cs-title-to-content" style={faqStyles.heading}>
                  Frequently Asked <span style={faqStyles.headingRed}>Questions.</span>
                </h2>
              </motion.div>

              {/* FAQ Items */}
              <div className="cs-content">
                {faqs.map((faq) => {
                  const isOpen = activeId === faq.id;
                  return (
                    <motion.div
                      key={faq.id}
                      initial={false}
                      animate={{ backgroundColor: isOpen ? '#fff' : 'rgba(255,255,255,0.6)' }}
                      className="mb-4 rounded-4 shadow-sm overflow-hidden"
                      style={{
                        border: '1px solid #eee',
                        borderLeft: `5px solid ${isOpen ? brandRed : brandNavy}`,
                        transition: 'border-left 0.3s ease',
                      }}
                    >
                      {/* Question Button */}
                      <button
                        className="faq-question-btn"
                        onClick={() => setActiveId(isOpen ? null : faq.id)}
                        style={faqStyles.btnBase}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <span style={{ color: isOpen ? brandRed : brandNavy, flexShrink: 0 }}>
                            {faq.icon}
                          </span>
                          <span style={faqStyles.questionText}>
                            {faq.question}
                          </span>
                        </div>
                        <motion.div
                          animate={{ rotate: isOpen ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                          style={{ flexShrink: 0, marginLeft: '12px' }}
                        >
                          <ChevronDown color={isOpen ? brandRed : '#ccc'} />
                        </motion.div>
                      </button>

                      {/* Answer */}
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                          >
                            <div
                              className="faq-answer-text"
                              style={faqStyles.answerText}
                            >
                              {faq.answer}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default Faq;
